// covered-by: frontends/carrie/src/routes/ask-puntt/hooks/__tests__/useAutoScroll.test.ts
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const NEAR_BOTTOM_THRESHOLD = 100;

/**
 * Pins a scroll container to the bottom while content grows, the way every
 * production chat (ChatGPT, Claude, Linear) does it:
 *
 *   1. Direct `scrollTop = scrollHeight - clientHeight` on the scroll
 *      container — instant, lands exactly at the bottom, no animation queue
 *      delay or "smooth-scroll startup latency" that `scrollIntoView({
 *      behavior: "smooth" })` ships with.
 *   2. A `ResizeObserver` on the scroll container *and* the inner content
 *      element catches every async size change (streamed text, image loads,
 *      web-font swaps, lazy-loaded children, killer-flow typewriter reveals
 *      growing the agent-step list) so the viewport stays pinned even when
 *      no React dep changes.
 *   3. A `useLayoutEffect` keyed on the explicit React deps still pins
 *      synchronously pre-paint on the first render of a chat and on every
 *      known reveal frame, so the first paint of a chat is *always* at the
 *      bottom rather than a post-paint scroll animation.
 *   4. `userScrolledUpRef` makes the pin sticky-but-respectful: once the
 *      user manually scrolls up the auto-pin pauses, and resumes only when
 *      they scroll back near the bottom (or `resetScroll()` is called on
 *      send, or `resetKey` changes to a new chat).
 *
 * Exported names preserved for back-compat with existing test mocks:
 * `scrollContainerRef`, `sentinelRef`, `handleScroll`, `resetScroll`,
 * `isScrolledUp`. `contentRef` is new — attach it to the element whose
 * height grows during streaming.
 */
export type UseAutoScrollOptions = {
  /**
   * When false, the viewport is not pinned as content grows — e.g. while an
   * agent response is streaming so the user can read earlier messages without
   * being pulled to the bottom on every token.
   */
  enabled?: boolean;
  /**
   * True while the local user's own turn is being submitted (ask-v2 passes
   * `transport.sending`). The rising edge pins to the bottom EVEN when
   * `enabled` is false.
   *
   * `enabled: false` exists so a streaming reply cannot drag a reader along
   * token by token — but a sent turn is already in flight the instant it is
   * submitted, so that hold also covers the message the user just wrote, and
   * leaves it below the fold. A send is a discrete, user-initiated event, so
   * it outranks the hold: it clears the sticky-up state and pins.
   *
   * This is deliberately a signal from the local send path, not a growth
   * count: a turn arriving from another member in a shared feed must never
   * yank the reader down.
   */
  sentSignal?: boolean;
  /**
   * Bumps when the caller re-activates the scroller (ask-v2: the reader
   * left the thread and came back). The rising value pins EVEN when
   * `enabled` is false — a turn still in flight would otherwise re-latch
   * from the parked offset in the same commit.
   *
   * A number, not a boolean edge the caller computes and the hook re-derives,
   * so the value can sit in the layout effect's dependency list. Arming a ref
   * after this hook returns does not: the effect may not re-run, and the
   * one-shot is never consumed.
   */
  resetSignal?: number;
};

export function useAutoScroll(
  deps: unknown[],
  /**
   * When this value changes (e.g. the active chat id), sticky-up state
   * resets so the next layout effect jumps the new chat instantly to the
   * bottom, not wherever the previous chat happened to leave the scroll
   * position.
   */
  resetKey?: unknown,
  options?: UseAutoScrollOptions,
) {
  const enabled = options?.enabled ?? true;
  const sentSignal = options?.sentSignal === true;
  const resetSignal = options?.resetSignal ?? 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  // Opt-in, caller-driven suppression (distinct from userScrolledUpRef, which
  // tracks manual scroll position). While paused, every growth-triggered pin
  // is a no-op so a user reading content near the bottom (e.g. a streaming
  // review checklist revealing items) is not yanked down. The position-based
  // userScrolledUpRef stays untouched, so resuming restores normal pinning.
  const isPausedRef = useRef(false);
  const lastObservedContentHeightRef = useRef(0);
  const prevResetKeyRef = useRef(resetKey);
  const prevSentSignalRef = useRef(false);
  const prevResetSignalRef = useRef(resetSignal);
  // why: `resetKey` itself cannot be a layout-effect dependency. Full-bleed
  // swaps it for a Symbol, and when `deps` also changes length React
  // stringifies the list and throws "Cannot convert a Symbol value to a
  // string". A number bumps in the same render and is what the effect lists.
  const resetEpochRef = useRef(0);
  // Armed during render, consumed by the layout effect. A ref, not a render
  // comparison, so a StrictMode double render cannot eat the one-shot: arming
  // twice is idempotent, whereas consuming "changed" twice is not.
  const forcePinOnSendRef = useRef(false);
  // why: clearing the sticky-up latch during render is not enough. This same
  // commit's layout effect calls pinToBottom, and while `enabled` is false
  // that takes the branch that re-derives the latch from the container's
  // parked offset (`syncScrollStickyState`). A reader coming back to a thread
  // mid-stream — or a resetKey change while a turn is in flight — would be
  // latched again before anything could pin. Consumed below, before that
  // branch, the same way `forcePinOnSendRef` is.
  const forcePinOnResetRef = useRef(false);

  let resetEpoch = resetEpochRef.current;
  if (prevResetKeyRef.current !== resetKey) {
    prevResetKeyRef.current = resetKey;
    userScrolledUpRef.current = false;
    lastObservedContentHeightRef.current = 0;
    forcePinOnResetRef.current = true;
    resetEpoch += 1;
    resetEpochRef.current = resetEpoch;
  }

  // Same one-shot as a resetKey change. The caller bumps `resetSignal` when
  // the scroller comes back; the value is a layout-effect dependency below,
  // which is what makes this commit actually consume the one-shot.
  if (prevResetSignalRef.current !== resetSignal) {
    prevResetSignalRef.current = resetSignal;
    userScrolledUpRef.current = false;
    lastObservedContentHeightRef.current = 0;
    forcePinOnResetRef.current = true;
  }

  // Rising edge only: `sentSignal` stays true for the whole request, and the
  // send must pin once, not on every render while it is in flight.
  if (sentSignal && !prevSentSignalRef.current) {
    forcePinOnSendRef.current = true;
  }
  prevSentSignalRef.current = sentSignal;

  const setAutoScrollPaused = useCallback((paused: boolean) => {
    isPausedRef.current = paused;
  }, []);

  const syncScrollStickyState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return false;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;
    userScrolledUpRef.current = !nearBottom;
    return userScrolledUpRef.current;
  }, []);

  const pinToBottom = useCallback(() => {
    // Caller-driven pause (e.g. user reading a streaming review checklist):
    // skip the pin entirely without disturbing position-based sticky state.
    if (isPausedRef.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    if (!enabled) {
      syncScrollStickyState();
      return;
    }
    if (userScrolledUpRef.current) return;
    // Direct write — no animation, no queue. scrollHeight already accounts
    // for the container's own top/bottom padding so this lands at the true
    // bottom of the scroll area, not short of it.
    el.scrollTop = el.scrollHeight - el.clientHeight;
  }, [enabled, syncScrollStickyState]);

  const handleScroll = useCallback(() => {
    return syncScrollStickyState();
  }, [syncScrollStickyState]);

  const resetScroll = useCallback(() => {
    userScrolledUpRef.current = false;
  }, []);

  // Pin synchronously pre-paint on every React-known change. This is what
  // makes the first paint of a chat land at the bottom rather than animate
  // there post-paint.
  useLayoutEffect(() => {
    // A send outranks a held viewport. `pinToBottom` is a no-op while
    // `enabled` is false, and the turn is in flight from the instant it is
    // submitted — so without this the message the user just wrote would be
    // appended below the fold and stay there. Writing the scroll here clears
    // the sticky-up state: composing a message is an explicit ask to see it.
    // `forcePinOnResetRef` shares the write: a resetKey change or a
    // `resetSignal` bump (the reader came back to the thread) has the same
    // problem, and `pinToBottom`'s `!enabled` branch would re-latch from the
    // parked offset in this same commit. `resetEpoch` and `resetSignal` are
    // dependencies so this effect re-runs in that commit — the one-shot is
    // armed during render, and a ref arm alone does not schedule it.
    // `resetKey` is not a dependency: full-bleed passes a Symbol, and React
    // stringifies a dependency list whose length changed.
    //
    // Deliberately outranks BOTH of pinToBottom's suppressions, not just
    // `enabled`: `isPausedRef` is not checked either. Every pause source is a
    // content-growth heuristic — `useAutoScrollPauseRegistry` aggregates
    // viewport-intersection reports from in-progress widgets (thinking row,
    // streaming review checklist) so streamed tokens do not drag a reader
    // along. A local send is not growth arriving at the reader; it is the
    // reader's own discrete action, and the one thing they asked to see. A
    // paused surface that swallowed the send would leave the author's message
    // off-screen for as long as some unrelated widget kept streaming.
    if (forcePinOnSendRef.current || forcePinOnResetRef.current) {
      const el = scrollContainerRef.current;
      if (el) {
        forcePinOnSendRef.current = false;
        forcePinOnResetRef.current = false;
        userScrolledUpRef.current = false;
        el.scrollTop = el.scrollHeight - el.clientHeight;
        return;
      }
      // The scrollable element is not in this commit. The review workspace
      // shows its composer on both panes but mounts the feed container only
      // on the feed pane, so a send from the comments pane arrives here.
      // Disarming now would drop the send silently and leave the thread
      // opening at the top; stay armed and let the next layout effect — the
      // pane switch is in the callers' deps — honour it.
    }
    pinToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinToBottom, enabled, sentSignal, resetEpoch, resetSignal, ...deps]);

  // ResizeObserver covers growth React can't see: streamed text landing,
  // <img>s loading, fonts swapping, agent-step rows expanding, the killer-
  // flow typewriter reveal pacing in new blocks. We observe BOTH the scroll
  // container (clientHeight changes — viewport resize, sibling panes
  // collapsing) and the inner content element (scrollHeight changes —
  // messages growing).
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const scroller = scrollContainerRef.current;
    if (!scroller) return;

    const ro = new ResizeObserver(() => {
      const content = contentRef.current;
      const contentHeight =
        content?.scrollHeight ?? scrollContainerRef.current?.scrollHeight ?? 0;
      const contentGrew = contentHeight > lastObservedContentHeightRef.current;
      lastObservedContentHeightRef.current = contentHeight;

      // Viewport-only resizes (e.g. the canvas pane opening and narrowing the
      // chat column) must not yank the scroll position — only new message
      // content growing should pin.
      if (!enabled || !contentGrew) {
        syncScrollStickyState();
        return;
      }
      pinToBottom();
    });
    ro.observe(scroller);
    const content = contentRef.current;
    if (content) ro.observe(content);

    return () => ro.disconnect();
  }, [enabled, pinToBottom, syncScrollStickyState]);

  return {
    scrollContainerRef,
    sentinelRef,
    contentRef,
    handleScroll,
    resetScroll,
    /**
     * Opt-in pause: while `true`, content growth no longer re-pins the
     * viewport to the bottom. Resume with `false`. Used to let users read a
     * streaming review checklist without being scrolled away.
     */
    setAutoScrollPaused,
    /** Exposed for testing — true when user has scrolled away from bottom. */
    get isScrolledUp() {
      return userScrolledUpRef.current;
    },
  };
}
